/**
 * Webinar embed — the one state-driven block for the webinar page.
 *
 * Reads the ?canview token from the URL:
 *   - no token   → public countdown to the next occurrence (target refreshed from /api/yeeter-next.php
 *                  so the cached page can't freeze it); "live" message while running. No video.
 *   - with token → POST /api/yeeter-unlock.php validates the HMAC SERVER-SIDE, then: upcoming → count
 *                  down to that occurrence, live → reveal the video, ended/invalid → a message + a back
 *                  button (the page never redirects, so the visitor sees why).
 *
 * The video id lives in this block's markup (data-video-id); only the *display* is gated by the server
 * verdict. Provider-agnostic (YouTube now, Vimeo later; Bunny.net can slot into embedUrl()).
 */
import { renderDigits } from './webinar-countdown.js';

const FLEX = { checking: true, ended: true, denied: true };

function panels(root) {
  const q = (sel) => root.querySelector(sel);
  return {
    checking: q('[data-we-checking]'),
    timer: q('[data-we-timer]'),
    live: q('[data-we-live]'),
    video: q('[data-we-video]'),
    ended: q('[data-we-ended]'),
    denied: q('[data-we-denied]'),
    // The preview facade is shown ALONGSIDE the timer (not part of the single-panel switch below).
    preview: q('[data-we-preview]'),
  };
}

function show(p, which) {
  Object.entries(p).forEach(([k, el]) => {
    if (!el || k === 'preview') return; // preview is toggled separately (co-visible with the timer)
    const on = k === which;
    el.classList.toggle('hidden', !on);
    if (FLEX[k]) el.classList.toggle('flex', on);
  });
}

// The 5-min preview shows only in the pre-live (upcoming) states; hidden once the full video/live/
// ended/denied/checking states take over.
function setPreview(p, on) {
  if (p.preview) p.preview.classList.toggle('hidden', !on);
}

/** Strip a full URL down to a provider id, or pass a bare id through. */
function parseId(provider, raw) {
  const v = (raw || '').trim();
  if (!v || !/^https?:\/\//i.test(v)) return v;
  try {
    const u = new URL(v);
    if (provider === 'vimeo') {
      const m = u.pathname.match(/(\d+)/);
      return m ? m[1] : v;
    }
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const parts = u.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1] || v;
  } catch (e) {
    return v;
  }
}

function embedUrl(provider, id) {
  if (provider === 'vimeo') {
    return `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1`;
  }
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&modestbranding=1`;
}

function buildVideo(container, provider, id) {
  if (!id || !container || container.dataset.built) return;
  container.dataset.built = '1';
  const wrap = document.createElement('div');
  wrap.className = 'relative w-full overflow-hidden rounded-xl';
  wrap.style.aspectRatio = '16 / 9';
  const iframe = document.createElement('iframe');
  iframe.src = embedUrl(provider, id);
  iframe.title = 'Webinar';
  iframe.allow =
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
  iframe.allowFullscreen = true;
  iframe.className = 'absolute inset-0 h-full w-full border-0';
  wrap.appendChild(iframe);
  container.appendChild(wrap);
}

function runEngine(root, startTs, lengthS, now, onState) {
  const endTs = startTs + lengthS;
  const tick = () => {
    const t = now();
    let state;
    if (t < startTs) {
      state = 'upcoming';
      renderDigits(root, startTs - t);
    } else if (t < endTs) {
      state = 'live';
    } else {
      state = 'ended';
    }
    onState(state);
    if (state === 'ended') {
      clearInterval(root.__weTimer);
      root.__weTimer = null;
    }
  };
  if (root.__weTimer) clearInterval(root.__weTimer);
  tick();
  root.__weTimer = setInterval(tick, 1000);
}

function publicMode(root, p) {
  const ms = root.dataset.ms || '';
  const endpoint = root.dataset.nextEndpoint || '/api/yeeter-next.php';
  let start = parseInt(root.dataset.next || '0', 10);
  let length = parseInt(root.dataset.length || '3600', 10);
  let now = () => Date.now() / 1000;

  const run = () =>
    runEngine(root, start, length, now, (state) => {
      if (state === 'upcoming') {
        show(p, 'timer');
        setPreview(p, true);
      } else if (state === 'live') {
        show(p, 'live');
        setPreview(p, false);
      } else {
        load(); // ended → re-fetch the following occurrence
      }
    });

  const load = async () => {
    try {
      const res = await fetch(`${endpoint}?ms=${encodeURIComponent(ms)}`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok && data.next_start) {
          start = data.next_start;
          length = data.length || length;
          if (data.server_now) {
            const skew = data.server_now - Date.now() / 1000;
            now = () => Date.now() / 1000 + skew;
          }
        }
      }
    } catch (e) {
      /* fall back to the SSR hint in data-next */
    }
    if (!start) return; // nothing scheduled
    run();
  };
  load();
}

function tokenMode(root, p, token) {
  const endpoint = root.dataset.endpoint || '/api/yeeter-unlock.php';
  const provider = root.dataset.provider || 'youtube';
  const id = parseId(provider, root.dataset.videoId || '');
  show(p, 'checking');
  setPreview(p, false);

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ canview: token }),
  })
    .then((r) => r.json())
    .then((data) => {
      if (!data || !data.valid) {
        show(p, 'denied');
        return;
      }
      const skew = data.server_now ? data.server_now - Date.now() / 1000 : 0;
      const now = () => Date.now() / 1000 + skew;
      runEngine(root, data.occurrence_start, data.length, now, (state) => {
        if (state === 'upcoming') {
          show(p, 'timer');
          setPreview(p, true);
        } else if (state === 'live') {
          show(p, 'video');
          setPreview(p, false);
          buildVideo(p.video, provider, id);
        } else {
          show(p, 'ended');
          setPreview(p, false);
        }
      });
    })
    .catch(() => show(p, 'denied'));
}

export function webinarEmbed() {
  const root = document.querySelector('[data-webinar-embed]');
  if (!root) return; // not a webinar page
  const p = panels(root);

  // "Back" links just drop ?canview and re-show the current page (its public countdown).
  const back = new URL(window.location.href);
  back.searchParams.delete('canview');
  const backHref = back.pathname + back.search + back.hash;
  root.querySelectorAll('[data-we-back]').forEach((a) => {
    a.setAttribute('href', backHref);
  });

  const token = new URLSearchParams(window.location.search).get('canview') || '';
  if (token) tokenMode(root, p, token);
  else publicMode(root, p);
}
